import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../../lib/constants';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft } from '../../../../utils/draftUtils';

/**
 * Unified Patient service hook — Add or Edit based on uhid_no presence.
 */
export const usePatient = (uhid_no) => {
  const { showNotification } = useNotification();
  const isEdit = !!uhid_no;
  const [loading, setLoading] = useState(true);
  const [existingData, setExistingData] = useState(null);
  const [nextUhid, setNextUhid] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API_BASE_URL}/get_patient/${uhid_no}`)
        .then(res => {
          const d = res.data;
          const dob = d.date_of_birth ? new Date(d.date_of_birth).toISOString().split('T')[0] : '';
          setExistingData({
            name: String(d.name || ''), title: String(d.title || ''), email: String(d.email || ''),
            mobile: String(d.mobile || ''), date_of_birth: dob, address: String(d.address || ''),
            attendantName: String(d.attendant_name || ''), attendantPhone: String(d.attendant_phone || ''),
            attendantRelation: String(d.attendant_relation || ''), 
            insuranceAvailable: Number(d.insurance_available) === 1 || d.insurance_available === true || String(d.insurance_available).toLowerCase() === 'true',
            anyAllergies: Number(d.any_allergies) === 1 || d.any_allergies === true || String(d.any_allergies).toLowerCase() === 'true',
            sex: String(d.sex || ''), age: d.age || '',
            uhidNo: String(d.uhid_no || ''), payment_method: String(d.payment_method || ''),
            online_payment_option: String(d.online_payment_option || ''), card_type: String(d.card_type || ''),
            mentionAllergies: Array.isArray(d.mention_allergies) ? d.mention_allergies
              : typeof d.mention_allergies === 'string' && d.mention_allergies.length > 0
                ? d.mention_allergies.split(',').map(s => s.trim()) : [],
          });
        })
        .catch(() => showNotification('error', 'Failed to load patient data.'))
        .finally(() => setLoading(false));
    } else {
      axios.get(`${API_BASE_URL}/next_uhid`)
        .then(res => { if (res.data.next_uhid) setNextUhid(res.data.next_uhid); })
        .catch(() => showNotification('error', 'Failed to generate next UHID.'))
        .finally(() => { setLoading(false); setDataLoaded(true); });
    }
  }, [uhid_no, isEdit, showNotification]);

  const getDraft = (uhidNo) => {
    const key = uhidNo || 'default';
    return hasDraft(DRAFT_KEYS.ADD_PATIENT, key) ? loadDraft(DRAFT_KEYS.ADD_PATIENT, key) : null;
  };

  const clearSavedData = (uhidNo) => {
    const key = uhidNo || 'default';
    sessionStorage.removeItem(`addPatient_form_${key}`);
    sessionStorage.removeItem(`formStatus_addPatient_${key}`);
    clearDraft(DRAFT_KEYS.ADD_PATIENT, key);
  };

  const submit = async (payload) => isEdit
    ? axios.put(`${API_BASE_URL}/update_patient/${uhid_no}`, payload, { withCredentials: true })
    : axios.post(`${API_BASE_URL}/register_patient`, payload, { withCredentials: true });

  return { isEdit, loading, existingData, nextUhid, dataLoaded, getDraft, clearSavedData, submit };
};

/**
 * Patient List service hook — fetch all + delete.
 */
export const usePatientList = () => {
  const { showNotification } = useNotification();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPatients = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/list_patients`, { withCredentials: true })
      .then(res => setPatients(Array.isArray(res.data) ? res.data : []))
      .catch(() => showNotification('error', 'Failed to fetch patient records.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPatients(); }, []);

  const deletePatient = async (uhid_no) => {
    const res = await axios.delete(`${API_BASE_URL}/delete_patient/${uhid_no}`, { withCredentials: true });
    if (res.status === 200) { setPatients(prev => prev.filter(p => p.uhid_no !== uhid_no)); }
  };

  return { patients, loading, fetchPatients, deletePatient };
};
